import React, { useState, useEffect } from 'react';
import { Container, Button, Badge, Alert, Spinner } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import '../../styles/hub.css';

const SystemHub = () => {
  const { user, userProfile, userSystems, isAdmin, logout, loading } = useAuth();
  const [showUserDropdown, setShowUserDropdown] = useState(false);
  const [showRetryIndicator, setShowRetryIndicator] = useState(false);
  const navigate = useNavigate();

  // Detectar quando está acontecendo retry automático
  useEffect(() => {
    if (user && userSystems.length === 0 && !loading) {
      setShowRetryIndicator(true);
      
      // Esconder indicador após um tempo
      const timer = setTimeout(() => {
        setShowRetryIndicator(false);
      }, 10000);
      
      return () => clearTimeout(timer);
    } else {
      setShowRetryIndicator(false);
    }
  }, [user, userSystems.length, loading]);

  const handleSystemAccess = (system) => {
    if (system.inDevelopment) {
      alert('Sistema em desenvolvimento');
      return;
    }

    // Redirecionar para sistema externo ou página interna
    if (system.route.startsWith('http')) {
      window.location.href = system.route;
    } else {
      navigate(system.route);
    }
  };

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/login');
    } catch (error) {
      console.error('Erro durante logout:', error);
    }
  };

  const formatUserName = () => {
    if (userProfile?.full_name) {
      const names = userProfile.full_name.split(' ');
      if (names.length >= 2) {
        return `${names[0]} ${names[names.length - 1]}`;
      }
      return names[0];
    }
    return user?.email?.split('@')[0] || 'Usuário';
  };

  // Sistemas disponíveis baseados nos acessos do usuário
  const availableSystems = userSystems
    .filter(access => access.systems?.active)
    .map(access => ({
      id: access.systems.slug,
      name: access.systems.name,
      description: access.systems.description,
      icon: access.systems.icon || '🔧',
      color: access.systems.color || '#007bff',
      route: access.systems.route || '#',
      role: access.system_roles?.name,
      inDevelopment: !access.systems.route || access.systems.route === '#'
    }));

  return (
    <div className="system-hub">
      {/* Header */}
      <div className="hub-header">
        <Container>
          <div className="d-flex justify-content-between align-items-center">
            <div className="hub-logo-section">
              <div className="hub-logo">
                MM
              </div>
              <div>
                <h1 className="hub-title">Hub de Sistemas</h1>
                <p className="hub-subtitle">MM Eventos</p>
              </div>
            </div>
            
            <div className="hub-user-section">
              <div className="hub-user-info">
                <div className="hub-user-name">Olá, {formatUserName()}!</div>
                <div className="hub-user-email">{user?.email}</div>
                {isAdmin && (
                  <Badge bg="danger" className="mt-1">Administrador</Badge>
                )}
              </div>
              
              <div className="user-dropdown-container">
                <button 
                  className="user-dropdown-trigger"
                  onClick={() => setShowUserDropdown(!showUserDropdown)}
                >
                  <div className="user-avatar">
                    {formatUserName()?.charAt(0).toUpperCase() || 'U'}
                  </div>
                </button>
                
                {showUserDropdown && (
                  <div className="user-dropdown-menu">
                    <div className="user-dropdown-header">
                      <div className="user-dropdown-name">{userProfile?.full_name || formatUserName()}</div>
                      <div className="user-dropdown-email">{user?.email}</div>
                    </div>
                    
                    <div className="user-dropdown-divider"></div>
                    
                    <button 
                      className="user-dropdown-item" 
                      onClick={() => window.location.reload()}
                    >
                      <span className="user-dropdown-icon">🔄</span>
                      Recarregar Página
                    </button>
                    
                    {isAdmin && (
                      <>
                        <div className="user-dropdown-divider"></div>
                        <button 
                          className="user-dropdown-item" 
                          onClick={() => navigate('/admin/users')}
                        >
                          <span className="user-dropdown-icon">👥</span>
                          Gerenciar Usuários
                        </button>
                      </>
                    )}
                    
                    <div className="user-dropdown-divider"></div>
                    <button className="user-dropdown-item logout" onClick={handleLogout}>
                      <span className="user-dropdown-icon">🚪</span>
                      Sair
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </Container>
      </div>

      <div className="hub-content">
        <Container>
          {/* Indicador de retry automático */}
          {showRetryIndicator && (
            <Alert variant="info" className="mb-4">
              <div className="d-flex align-items-center">
                <Spinner animation="border" size="sm" className="me-3" />
                <div>
                  <strong>🔄 Carregando sistemas...</strong>
                  <br />
                  <small>O sistema está tentando automaticamente carregar seus acessos. Aguarde alguns segundos.</small>
                </div>
              </div>
            </Alert>
          )}

          {/* Título da seção */}
          <div className="mb-4">
            <h2 className="hub-section-title">
              Selecione o sistema que deseja acessar
            </h2>
            <p className="hub-section-subtitle">
              {loading 
                ? 'Carregando seus sistemas...'
                : availableSystems.length > 0 
                  ? `Você tem acesso a ${availableSystems.length} sistema${availableSystems.length !== 1 ? 's' : ''}`
                  : 'Verificando seus acessos...'
              }
            </p>
          </div>

          {/* Grid de Sistemas */}
          {availableSystems.length > 0 ? (
            <div className="hub-systems-grid">
              {availableSystems.map((system) => (
                <div 
                  key={system.id}
                  className={`hub-system-card ${system.inDevelopment ? 'system-disabled' : ''}`}
                  onClick={() => handleSystemAccess(system)}
                  style={{ cursor: system.inDevelopment ? 'not-allowed' : 'pointer' }}
                >
                  {/* Ícone do Sistema */}
                  <div className="hub-system-icon-no-bg">
                    {system.icon}
                  </div>

                  {/* Nome do Sistema */}
                  <h3 className="hub-system-name">
                    {system.name}
                  </h3>

                  {/* Descrição */}
                  <p className="hub-system-description">
                    {system.description}
                  </p>

                  {/* Role do usuário */}
                  <div className="mb-3">
                    <Badge bg="secondary">{system.role}</Badge>
                  </div>

                  {/* Botão de Acesso */}
                  <button 
                    className="hub-access-btn"
                    disabled={system.inDevelopment}
                  >
                    {system.inDevelopment ? 'Em Desenvolvimento' : 'Acessar Sistema'}
                  </button>

                  {/* Status */}
                  <div className="hub-access-status" style={{
                    color: system.inDevelopment ? '#fd7e14' : '#28a745'
                  }}>
                    {system.inDevelopment ? 'Sistema em Desenvolvimento' : 'Acesso Autorizado'}
                  </div>
                </div>
              ))}
            </div>
          ) : !loading && !showRetryIndicator ? (
            <div className="hub-no-systems">
              <h3>Nenhum sistema disponível</h3>
              <p>
                Entre em contato com o administrador para solicitar acesso aos sistemas.
              </p>
              <Button 
                variant="outline-primary"
                onClick={() => window.location.reload()}
              >
                🔄 Recarregar Página
              </Button>
            </div>
          ) : null}

          {/* Footer */}
          <div className="hub-footer">
            <p className="hub-footer-text">
              © {new Date().getFullYear()} MM Eventos® Health Division
            </p>
          </div>
        </Container>
      </div>
    </div>
  );
};

export default SystemHub;